// Pilot UAT + investor demo readiness primitives.
// All components are READ-ONLY indicators / walkthroughs / checklists that
// reuse existing dashboards, route structure, and geo-api utilities. They
// implement NO backend logic and contain NO large fake datasets.
//
// Exports:
//   <PilotDemoBanner />            — fixed indicator when APP_ENV !== 'production'
//   <InvestorOnboardingWalkthrough /> — dismissable first-run checklist (localStorage)
//   <ApiConnectionStatus />        — pings geo-api base for liveness
//   <PaymentReadinessPanel />      — readiness summary for payments (no live keys)
//   <ApplicationTrackingTimeline /> — compact timeline (reads existing data)
//   <SupportReadinessPanel />      — quick links into existing support tab
//   <PublicContentPreviewBadge />  — small badge surfaced when preview mode is on
//   <PilotDemoScenariosCard />     — demo scenario index for UAT
//
// Reuses: existing PortalCards, dashboards, AccessGovernance, PaymentsCenter,
// SupportCenter components — by linking/tab-switching, not duplicating.
//
// HF1-FIX1: no credential or implementation diagnostics are rendered here (the former
// DiagnosticsPanel / PortalReadinessChecklist are removed), and GIS access is never
// inferred from KYC or agreements — Investor GIS state comes only from the server
// entitlement contract (see InvestorGisEntry).

import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Database,
  CreditCard,
  FileSignature,
  Info,
  LifeBuoy,
  Map as MapIcon,
  Radio,
  ShieldCheck,
  Sparkles,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react';
import { APP_ENV, probeApiLiveness } from '@/lib/geoApi';
import { isProbeHealthy } from '@/lib/apiOrigin';
import { useAuth } from '@/contexts/AuthContext';
import type { InvestorProfileSummary, ApplicationRecord, ReviewTimelineEntry } from '@/services/investorService';

/* ───────────────── PilotDemoBanner ───────────────── */

export const PilotDemoBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState(false);
  if (APP_ENV === 'production') return null;
  if (dismissed) return null;
  // No "demo"/"test" terminology is ever displayed (Pilot Production rule).
  const label = APP_ENV === 'staging' ? 'PILOT / UAT' : 'PILOT VALIDATION';
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-eds-md bg-eds-warning/90 text-eds-fg shadow-lg border border-eds-warning text-xs font-semibold"
    >
      <Sparkles className="w-4 h-4" aria-hidden="true" />
      <span>{label} ENVIRONMENT — controlled operations</span>
      <button
        onClick={() => setDismissed(true)}
        className="ml-1 hover:bg-eds-warning/20 rounded-eds-sm p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
        aria-label="Dismiss pilot environment indicator"

      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/* ───────────────── ApiConnectionStatus ───────────────── */

type ApiState = 'checking' | 'online' | 'offline';

export const ApiConnectionStatus: React.FC = () => {
  const [state, setState] = useState<ApiState>('checking');

  const ping = async () => {
    setState('checking');
    setState(isProbeHealthy(await probeApiLiveness()) ? 'online' : 'offline');
  };

  useEffect(() => {
    void ping();
  }, []);

  const tone =
    state === 'online'
      ? 'border-eds-success/30 bg-eds-success/10 text-eds-success'
      : state === 'offline'
      ? 'border-eds-danger/30 bg-eds-danger/10 text-eds-danger'
      : 'border-eds-border bg-eds-surface-2 text-eds-muted-fg';

  const Icon = state === 'online' ? Wifi : state === 'offline' ? WifiOff : Radio;

  return (
    <div className={`flex items-center justify-between gap-3 border rounded-eds-md p-4 ${tone}`} role="status" aria-live="polite">
      <div className="flex items-center gap-3 min-w-0">
        <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            {state === 'online' ? 'Service available' : state === 'offline' ? 'Service temporarily unavailable' : 'Checking service…'}
          </div>
          {state === 'offline' && <div className="text-xs">GIS data may not load until the service is available again.</div>}
        </div>
      </div>
      <button
        onClick={ping}
        className="text-xs font-medium px-2.5 py-1 rounded-eds-sm border border-current/30 hover:bg-eds-surface/60 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
      >
        Try again
      </button>
    </div>
  );
};

/* ───────────────── Onboarding walkthrough ───────────────── */

const ONBOARDING_STORAGE_KEY = 'giisdec.investor.onboarding.dismissed';

interface OnboardingStep {
  title: string;
  done: boolean;
  action?: { label: string; tab: string };
}

export interface InvestorOnboardingWalkthroughProps {
  profile: InvestorProfileSummary | null;
  applicationCount: number;
  onJump: (tab: string) => void;
}

export const InvestorOnboardingWalkthrough: React.FC<InvestorOnboardingWalkthroughProps> = ({
  profile,
  applicationCount,
  onJump,
}) => {
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(ONBOARDING_STORAGE_KEY) !== '1'; } catch { return true; }
  });
  if (!open) return null;

  const status = profile?.status ?? 'public_visitor';
  const verified = ['verified_investor', 'shortlisted_investor', 'approved_investor', 'strategic_partner', 'verified'].includes(status);

  const steps: OnboardingStep[] = [
    { title: 'Complete KYC / onboarding', done: verified, action: { label: 'Open KYC', tab: 'kyc' } },
    { title: 'Sign NDA', done: !!profile?.ndaSigned, action: { label: 'Sign NDA', tab: 'agreements' } },
    { title: 'Sign MOU (optional)', done: !!profile?.mouSigned, action: { label: 'Review MOU', tab: 'agreements' } },
    { title: 'Submit first application', done: applicationCount > 0, action: { label: 'Apply', tab: 'permits' } },
  ];

  const close = () => {
    try { localStorage.setItem(ONBOARDING_STORAGE_KEY, '1'); } catch { /* noop */ }
    setOpen(false);
  };

  return (
    <div className="border border-eds-success/30 bg-eds-success/10 rounded-eds-lg p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-eds-fg flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-eds-success" /> Investor onboarding walkthrough
          </h3>
          <p className="text-sm text-eds-muted-fg mt-1">
            Four steps to a complete investor profile. You can dismiss this — progress is tracked from your live profile.
          </p>
        </div>
        <button
          onClick={close}
          className="text-eds-muted-fg hover:text-eds-fg rounded-eds-sm p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
          aria-label="Dismiss onboarding walkthrough"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <ol className="space-y-2">
        {steps.map((s, i) => (
          <li key={i} className="flex items-center gap-3 bg-eds-surface border border-eds-success/20 rounded-eds-md px-3 py-2">
            {s.done ? (
              <CheckCircle2 className="w-5 h-5 text-eds-success flex-shrink-0" aria-hidden="true" />
            ) : (
              <Circle className="w-5 h-5 text-eds-muted-fg/50 flex-shrink-0" aria-hidden="true" />
            )}
            <span className={`text-sm flex-1 ${s.done ? 'text-eds-muted-fg line-through' : 'text-eds-fg'}`}>
              {i + 1}. {s.title}
            </span>
            {!s.done && s.action && (
              <button
                onClick={() => onJump(s.action!.tab)}
                className="text-xs font-medium text-eds-success hover:opacity-80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-sm"
              >
                {s.action.label}
              </button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

/* ───────────────── Application tracking timeline (compact) ───────────────── */

export const ApplicationTrackingTimeline: React.FC<{
  applications: ApplicationRecord[];
  timeline: ReviewTimelineEntry[];
}> = ({ applications, timeline }) => {
  const rows = timeline.slice(0, 5);
  if (rows.length === 0 && applications.length === 0) {
    return (
      <div className="border border-eds-border rounded-eds-md p-4 text-sm text-eds-muted-fg">
        No applications or review activity yet. Submit a permit, document or restricted-data request to begin tracking.
      </div>
    );
  }
  return (
    <div className="border border-eds-border rounded-eds-md p-4">
      <h4 className="font-semibold text-eds-fg text-sm mb-3">Recent application activity</h4>
      <ol className="space-y-2">
        {rows.length === 0 ? (
          <li className="text-xs text-eds-muted-fg">
            {applications.length} application(s) on file — awaiting review activity.
          </li>
        ) : (
          rows.map((t) => (
            <li key={t.id} className="text-xs text-eds-fg flex items-start gap-2">
              <span className="w-1.5 h-1.5 mt-1.5 rounded-eds-pill bg-eds-success flex-shrink-0" aria-hidden="true" />
              <span className="flex-1">
                <span className="font-medium">{t.step}</span>
                <span className="text-eds-muted-fg"> · {t.state}</span>
                {t.note && <> — {t.note}</>}
              </span>
              {t.at && <span className="text-eds-muted-fg">{new Date(t.at).toLocaleDateString()}</span>}
            </li>
          ))
        )}
      </ol>
    </div>
  );
};


/* ───────────────── Readiness summary cards ───────────────── */

const ReadinessCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  state: 'ready' | 'partial' | 'blocked';
  description: string;
  cta?: React.ReactNode;
}> = ({ icon: Icon, title, state, description, cta }) => {
  const tone =
    state === 'ready'
      ? 'border-eds-success/30 bg-eds-success/10'
      : state === 'partial'
      ? 'border-eds-warning/30 bg-eds-warning/10'
      : 'border-eds-danger/30 bg-eds-danger/10';
  const label =
    state === 'ready' ? 'Ready' : state === 'partial' ? 'Partial' : 'Blocked';
  const labelTone =
    state === 'ready'
      ? 'bg-eds-success text-eds-success-fg' // VUI1-FIX1: was text-white (2.51:1)
      : state === 'partial'
      ? 'bg-eds-warning text-eds-fg'
      : 'bg-eds-danger text-white';

  return (
    <div className={`border rounded-eds-md p-4 ${tone}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5 text-eds-fg" aria-hidden="true" />
        <h4 className="font-semibold text-eds-fg text-sm flex-1">{title}</h4>
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-eds-pill ${labelTone}`}>{label}</span>
      </div>
      <p className="text-xs text-eds-muted-fg">{description}</p>
      {cta && <div className="mt-2">{cta}</div>}
    </div>
  );
};

export const PaymentReadinessPanel: React.FC = () => (
  <ReadinessCard
    icon={CreditCard}
    title="Payment readiness"
    state="partial"
    description="Payments are made against GIISDEC invoices and verified manually by GIISDEC Finance. There is no online checkout in the Investor Portal."
  />
);

export const SupportReadinessPanel: React.FC<{ onJump?: (tab: string) => void }> = ({ onJump }) => (
  <ReadinessCard
    icon={LifeBuoy}
    title="Support readiness"
    state="ready"
    description="Investor support enquiries go to the GIISDEC support desk. Notification preferences are configurable per channel."
    cta={
      onJump ? (
        <button
          onClick={() => onJump('support')}
          className="text-xs font-medium text-eds-success hover:opacity-80 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-md"
        >
          Open support panel
        </button>
      ) : null
    }
  />
);

export const AgreementsReadinessPanel: React.FC<{
  profile: InvestorProfileSummary | null;
  onJump?: (tab: string) => void;
}> = ({ profile, onJump }) => {
  const nda = !!profile?.ndaSigned;
  const mou = !!profile?.mouSigned;
  const state: 'ready' | 'partial' | 'blocked' = nda && mou ? 'ready' : nda || mou ? 'partial' : 'blocked';
  return (
    <ReadinessCard
      icon={FileSignature}
      title="MOU / NDA / LOI"
      state={state}
      description={`NDA: ${nda ? 'signed' : 'pending'} · MOU: ${mou ? 'signed' : 'pending'} · LOI requests handled per project.`}
      cta={
        onJump ? (
          <button
            onClick={() => onJump('agreements')}
            className="text-xs font-medium text-eds-success hover:opacity-80 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-md"
          >
            Open agreements
          </button>
        ) : null
      }
    />
  );
};

/* ───────────────── Public content preview readiness ───────────────── */

export const PublicContentPreviewBadge: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  if (!enabled) return null;
  return (
    <div
      role="status"
      className="inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-eds-pill bg-eds-warning/10 text-eds-fg border border-eds-warning/30"
    >
      <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
      Preview content — not yet published
    </div>
  );
};

/* ───────────────── Pilot demo scenarios index ───────────────── */

export const PilotDemoScenariosCard: React.FC<{ onJump?: (tab: string) => void }> = ({ onJump }) => {
  const scenarios: { title: string; tab: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { title: 'Complete your investor profile', tab: 'kyc', icon: ShieldCheck },
    { title: 'Manage your KYC information', tab: 'kyc', icon: ShieldCheck },
    { title: 'Manage your documents', tab: 'documents', icon: FileSignature },
    { title: 'Track your applications', tab: 'tracking', icon: Database },
    { title: 'Request geological information', tab: 'restricted', icon: MapIcon },
    { title: 'Review invoices and payments', tab: 'payments', icon: CreditCard },
    { title: 'Get GIISDEC support', tab: 'support', icon: LifeBuoy },
  ];
  return (
    <div className="border border-eds-border rounded-eds-lg p-5 bg-eds-surface">
      <h3 className="font-semibold text-eds-fg mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-eds-success" /> Explore Your Investor Portal

      </h3>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {scenarios.map((s) => (
          <li key={s.title}>
            <button
              onClick={() => onJump?.(s.tab)}
              className="w-full text-left text-sm px-3 py-2 rounded-eds-md border border-eds-border bg-eds-surface hover:border-eds-success/40 hover:bg-eds-success/10 transition flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
            >
              <s.icon className="w-4 h-4 text-eds-success" aria-hidden="true" />
              <span className="flex-1">{s.title}</span>
              <span className="text-xs text-eds-muted-fg">open</span>

            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ───────────────── Safe content placeholder ───────────────── */

// Use this anywhere a public-content section would normally render an empty
// state during pilot/UAT — keeps layouts intact without inventing fake data.
export const SafeContentPlaceholder: React.FC<{
  title: string;
  hint?: string;
}> = ({ title, hint }) => (
  <div className="border border-dashed border-eds-border rounded-eds-md p-6 bg-eds-surface-2 text-center">
    <div className="inline-flex items-center gap-2 text-eds-muted-fg text-sm">
      <Info className="w-4 h-4" />
      <span className="font-medium">{title}</span>
    </div>
    {hint && <p className="text-xs text-eds-muted-fg mt-1">{hint}</p>}
  </div>
);

/* ───────────────── InvestorOperationalStatus ─────────────────
 *
 * Compact READ-ONLY operational status widget for the investor dashboard
 * overview. Polls geo-api once on mount for liveness, then derives module
 * availability from the resolved geo-api base, auth session, and profile
 * state. Never throws — partial API failures degrade gracefully into a
 * neutral "checking" chip.
 *
 * Layout: a single horizontal strip of 7 micro-chips that stack into a 2-col
 * grid on small screens. Mounts cleanly into the existing overview without
 * any redesign.
 */

type ChipState = 'ok' | 'partial' | 'down' | 'checking';

const StatusChip: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  state: ChipState;
  hint?: string;
}> = ({ icon: Icon, label, state, hint }) => {
  const tone =
    state === 'ok'
      ? 'border-eds-success/30 bg-eds-success/10 text-eds-success'
      : state === 'partial'
      ? 'border-eds-warning/30 bg-eds-warning/10 text-eds-warning'
      : state === 'down'
      ? 'border-eds-danger/30 bg-eds-danger/10 text-eds-danger'
      : 'border-eds-border bg-eds-surface-2 text-eds-muted-fg';
  const dotTone =
    state === 'ok'
      ? 'bg-eds-success'
      : state === 'partial'
      ? 'bg-eds-warning'
      : state === 'down'
      ? 'bg-eds-danger'
      : 'bg-eds-muted-fg animate-pulse';
  return (
    <div
      className={`flex items-center gap-2 px-2.5 py-2 rounded-eds-md border text-xs ${tone}`}
      role="status"
      aria-label={`${label}: ${state}${hint ? ` — ${hint}` : ''}`}
      title={hint || label}
    >
      <span className={`w-1.5 h-1.5 rounded-eds-pill flex-shrink-0 ${dotTone}`} aria-hidden="true" />
      <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
      <span className="font-medium truncate">{label}</span>
    </div>
  );
};

export const InvestorOperationalStatus: React.FC<{
  profile: InvestorProfileSummary | null;
  applicationCount?: number;
}> = ({ profile, applicationCount = 0 }) => {
  const { user, role } = useAuth();
  const [apiState, setApiState] = useState<ChipState>('checking');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await probeApiLiveness();
        if (cancelled) return;
        // NITA-02A R6: /health is unauthenticated — only a 2xx is available; a
        // 404 (wrong path), any other 4xx, a 5xx or a network failure is not.
        setApiState(isProbeHealthy(s) ? 'ok' : 'down');
      } catch {
        if (!cancelled) setApiState('down');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const authState: ChipState = user && role ? 'ok' : user ? 'partial' : 'down';
  const ndaOk = !!profile?.ndaSigned;
  const mouOk = !!profile?.mouSigned;
  const agreementsState: ChipState = ndaOk && mouOk ? 'ok' : ndaOk || mouOk ? 'partial' : 'partial';
  const paymentsState: ChipState = apiState === 'down' ? 'down' : 'partial';
  const supportState: ChipState = apiState === 'ok' ? 'ok' : apiState === 'down' ? 'down' : 'checking';

  const chips: { icon: React.ComponentType<{ className?: string }>; label: string; state: ChipState; hint?: string }[] = [
    { icon: Wifi, label: 'Service', state: apiState, hint: apiState === 'ok' ? 'available' : apiState === 'down' ? 'temporarily unavailable' : apiState === 'partial' ? 'sign-in required' : 'checking' },
    { icon: ShieldCheck, label: 'Session', state: authState, hint: role ? `role: ${role}` : 'role pending' },
    { icon: FileSignature, label: 'Agreements', state: agreementsState, hint: `NDA ${ndaOk ? 'signed' : 'pending'} · MOU ${mouOk ? 'signed' : 'pending'}` },
    { icon: CreditCard, label: 'Payments', state: paymentsState, hint: 'verified manually by GIISDEC Finance' },
    { icon: LifeBuoy, label: 'Support', state: supportState, hint: 'support desk & notifications' },
  ];

  return (
    <div className="border border-eds-border rounded-eds-lg p-4 bg-eds-surface">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-eds-fg text-sm flex items-center gap-2">
          <Radio className="w-4 h-4 text-eds-success" aria-hidden="true" /> Operational status
        </h3>
        <span className="text-[10px] font-medium text-eds-muted-fg uppercase tracking-wider">
          read-only · live
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
        {chips.map((c) => (
          <StatusChip key={c.label} icon={c.icon} label={c.label} state={c.state} hint={c.hint} />
        ))}
      </div>
    </div>
  );
};
